import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { getCorridorStats } from "@/lib/api";

// Abstract corridor visual: origin hubs → African hubs, with animated arcs
const NODES = [
  { id: "lon", label: "London", x: 42, y: 22, from: true },
  { id: "ams", label: "Amsterdam", x: 48, y: 26, from: true },
  { id: "nyc", label: "New York", x: 22, y: 32, from: true },
  { id: "par", label: "Paris", x: 47, y: 28, from: true },
  { id: "dxb", label: "Dubai", x: 63, y: 42, from: true },
  { id: "lag", label: "Lagos", x: 48, y: 62, to: true, country: "NG" },
  { id: "nbi", label: "Nairobi", x: 62, y: 68, to: true, country: "KE" },
  { id: "acc", label: "Accra", x: 42, y: 60, to: true, country: "GH" },
  { id: "jnb", label: "Johannesburg", x: 55, y: 84, to: true, country: "ZA" },
  { id: "cai", label: "Cairo", x: 58, y: 42, to: true, country: "EG" },
  { id: "dak", label: "Dakar", x: 36, y: 56, to: true, country: "SN" },
];

const PAIRS = [
  ["lon", "lag"],
  ["ams", "nbi"],
  ["nyc", "acc"],
  ["par", "dak"],
  ["dxb", "nbi"],
  ["lon", "jnb"],
  ["ams", "cai"],
];

export default function CorridorMap() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({ by_country: [], total_founders: 0 });
  useEffect(() => {
    getCorridorStats()
      .then(setStats)
      .catch(() => {});
  }, []);
  const countByCC = Object.fromEntries((stats.by_country || []).map((r) => [r.country, r.count]));

  return (
    <section className="py-24 md:py-32 bg-canvas" data-testid="corridor-map">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-4 lg:sticky lg:top-28">
          <div className="label-eyebrow">{t("home.corridorMap.eyebrow")}</div>
          <h2 className="mt-4 font-display font-medium text-forest text-4xl lg:text-5xl leading-[0.95] tracking-tightest">
            One <span className="italic font-serif">continent-shaped</span> network, wired in both directions.
          </h2>
          <p className="mt-6 text-ink/70 leading-relaxed">
            Every arc is a live corridor. Send from London to Lagos, from New York to Accra, from
            Amsterdam to Nairobi — or receive from any of them, into a bank, wallet or QR.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-6">
            <Kpi n="34" label="Countries" />
            <Kpi n="9" label="Currencies out" />
            <Kpi n="72" label="Currencies in" />
            <Kpi n={stats.total_founders || 0} label="Founders on map" testId="map-total-founders" />
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="card-flat aspect-[10/11] md:aspect-[10/9] relative overflow-hidden">
            {/* Grid lines */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
              {Array.from({ length: 10 }).map((_, i) => (
                <line
                  key={`v-${i}`}
                  x1={i * 10}
                  y1="0"
                  x2={i * 10}
                  y2="100"
                  stroke="#4A1D96"
                  strokeOpacity="0.05"
                  strokeWidth="0.1"
                />
              ))}
              {Array.from({ length: 10 }).map((_, i) => (
                <line
                  key={`h-${i}`}
                  x1="0"
                  y1={i * 10}
                  x2="100"
                  y2={i * 10}
                  stroke="#4A1D96"
                  strokeOpacity="0.05"
                  strokeWidth="0.1"
                />
              ))}
              {/* Continent silhouette (abstract) */}
              <path
                d="M40 42 Q35 52 42 62 Q40 74 55 82 Q64 76 60 70 Q66 60 60 52 Q60 44 55 42 Q52 34 45 38 Z"
                fill="#4A1D96"
                fillOpacity="0.05"
              />
              {/* Arcs */}
              {PAIRS.map(([a, b], idx) => {
                const A = NODES.find((n) => n.id === a);
                const B = NODES.find((n) => n.id === b);
                const midX = (A.x + B.x) / 2;
                const midY = Math.min(A.y, B.y) - 12;
                const d = `M ${A.x} ${A.y} Q ${midX} ${midY} ${B.x} ${B.y}`;
                return (
                  <g key={`${a}-${b}`}>
                    <motion.path
                      d={d}
                      stroke="#4A1D96"
                      strokeOpacity="0.35"
                      strokeWidth="0.25"
                      fill="none"
                      strokeDasharray="1.5 1.2"
                      initial={{ pathLength: 0 }}
                      whileInView={{ pathLength: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.4, delay: idx * 0.12, ease: "easeInOut" }}
                    />
                    <circle r="0.7" fill="#E8FF00">
                      <animateMotion
                        dur="3.5s"
                        repeatCount="indefinite"
                        path={d}
                        begin={`${idx * 0.4}s`}
                      />
                      <animate
                        attributeName="opacity"
                        values="0;1;1;0"
                        dur="3.5s"
                        repeatCount="indefinite"
                        begin={`${idx * 0.4}s`}
                      />
                    </circle>
                  </g>
                );
              })}
            </svg>

            {/* Nodes */}
            {NODES.map((n) => {
              const cnt = n.country ? countByCC[n.country] : 0;
              return (
                <div
                  key={n.id}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${n.x}%`, top: `${n.y}%` }}
                  data-testid={`node-${n.id}`}
                >
                  <div className="relative flex items-center gap-2">
                    <span
                      className={`w-2.5 h-2.5 rounded-full ${
                        n.to ? "bg-lime ring-4 ring-lime/20" : "bg-forest ring-4 ring-forest/15"
                      }`}
                    />
                    <span
                      className={`hidden md:inline text-[10px] tracking-[0.22em] uppercase ${
                        n.to ? "text-forest" : "text-ink/70"
                      }`}
                    >
                      {n.label}
                    </span>
                    {n.to && cnt > 0 && (
                      <motion.span
                        initial={{ opacity: 0, scale: 0.7 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4 }}
                        className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-forest text-canvas text-[9px] tracking-[0.18em] font-bold uppercase"
                        data-testid={`node-count-${n.country}`}
                      >
                        <span className="live-dot !w-1.5 !h-1.5" style={{ boxShadow: "none" }} />
                        {cnt} founder{cnt === 1 ? "" : "s"}
                      </motion.span>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Legend */}
            <div className="absolute left-6 bottom-6 flex items-center gap-4 text-[11px] text-ink/60">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-forest" /> Origin
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-lime" /> Payout
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Kpi({ n, label, testId }) {
  return (
    <div>
      <div className="font-display text-3xl md:text-4xl tracking-tightest text-forest" data-testid={testId}>{n}</div>
      <div className="mt-1 text-[10px] tracking-[0.22em] uppercase text-ink/50">{label}</div>
    </div>
  );
}
